import { useDroppable } from "@dnd-kit/core";
import KanbanCard from "../KanbanCard/KanbanCard";
import { Box, Skeleton } from "@mui/material";
import "./KanbanLane.scss";
import { useEffect, useRef, useState } from "react";

interface KanbanLaneProps {
  isLoading: boolean;
  title: string;
  items: any[];
  headerColor: string;
  activeCard: any;
  onCardClick: (id: number) => void;
}

export default function KanbanLane({
  title,
  items,
  headerColor,
  activeCard,
  onCardClick,
  isLoading,
}: KanbanLaneProps) {
  const { setNodeRef } = useDroppable({
    id: title,
  });

  const [areaLabel, setAreaLabel] = useState<string[]>([]);
  const [hasScroll, setHasScroll] = useState(false);
  const cardsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const formatted = items.map(({ hospital_areas = [], hospital_units = [] }) => {
      if (hospital_areas.length === 0 || hospital_units.length === 0) return "";

      const { area_name } = hospital_areas[0];
      const { name } = hospital_units[0];
      let str = `${area_name} • ${name}`;

      const extra = hospital_areas.length - 1;
      if (extra > 0) str += ` + ${extra}`;

      return str;
    });

    setAreaLabel(formatted);
  }, [items]);

  useEffect(() => {
    const el = cardsRef.current;
    if (!el) return;

    const checkScroll = () => {
      const vertical = el.scrollHeight > el.clientHeight;
      const horizontal = el.scrollWidth > el.clientWidth;
      setHasScroll(vertical || horizontal);
    };

    checkScroll();

    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);

    return () => observer.disconnect();
  }, [items]);

  const skeletonCount = useRef(Math.floor(Math.random() * 5) + 2).current;

  return (
    <Box className="lane">
      {isLoading ? (
        <Skeleton
          className="lane__header--skeleton"
          variant="rectangular"
          width={300}
          animation="wave"
        />
      ) : (
        <Box
          className="lane__header"
          sx={{
            backgroundColor: headerColor,
            color:
              title === "IDEA" || title === "EN REVISIÓN" || title === "BACKLOG"
                ? "black"
                : "white",
          }}
        >
          {title}
        </Box>
      )}

      <Box
        ref={(node: any) => {
          setNodeRef(node);
          cardsRef.current = node;
        }}
        className={hasScroll ? "lane__cards--scrollable" : "lane__cards"}
      >
        {isLoading
          ? Array.from({ length: skeletonCount }).map((_, i) => (
              <Skeleton
                key={i}
                animation="wave"
                variant="rectangular"
                className="lane__cards--skeleton"
              />
            ))
          : [...items]
              .sort(
                (a, b) =>
                  new Date(a.created_at).getTime() -
                  new Date(b.created_at).getTime()
              )
              .map(
                ({ title: cardTitle, created_at, authors, idea_id }, key) => (
                  <KanbanCard
                    area={areaLabel[key]}
                    authors={authors}
                    ideaDate={created_at}
                    idea_id={idea_id}
                    title={cardTitle}
                    key={idea_id}
                    index={key}
                    parent={title}
                    isDragging={activeCard?.idea_id === idea_id}
                    onCardClick={() => onCardClick(idea_id)}
                  />
                )
              )}
      </Box>
    </Box>
  );
}
